// Modules
import { components } from 'types/api';

// ============================================
// Types from Backend API (Auto-generated)
// ============================================

// DTOs from backend
export type CreateItemGroupDto = components['schemas']['CreateItemGroupDto'];
export type UpdateItemGroupDto = components['schemas']['UpdateItemGroupDto'];
export type CreateInventoryItemDto = components['schemas']['CreateInventoryItemDto'];
export type UpdateInventoryItemDto = components['schemas']['UpdateInventoryItemDto'];
export type StockInDto = components['schemas']['StockInDto'];
export type StockOutDto = components['schemas']['StockOutDto'];
export type StockAdjustmentDto = components['schemas']['StockAdjustmentDto'];

// ============================================
// Enums (matching backend values)
// ============================================

export enum InventoryUnit {
  PIECES = 'pieces',
  FEET = 'feet',
  METERS = 'meters',
  BOXES = 'boxes',
  BAGS = 'bags',
  GALLONS = 'gallons',
  EACH = 'each',
  ROLLS = 'rolls',
  SHEETS = 'sheets',
  POUNDS = 'pounds',
  KILOGRAMS = 'kilograms',
}

export enum StockMovementType {
  IN = 'IN',
  OUT = 'OUT',
  ADJUSTMENT = 'ADJUSTMENT',
}

// ============================================
// Entity Interfaces
// ============================================

export interface ItemVariant {
  _id: string;
  sku: string;
  attributes: Record<string, string>;
  quantity: number;
  allocatedQuantity?: number;
  unitCost: number;
  minStock?: number;
  isActive: boolean;
}

export interface ItemGroup {
  _id: string;
  name: string;
  description?: string;
  color?: string;
  parentGroup?: string | ItemGroup;
  company: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  subgroups?: ItemGroup[];
  itemCount?: number;
}

export interface Vendor {
  _id: string;
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  isPreferred: boolean;
  isActive: boolean;
  company: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

export interface InventoryItem {
  _id: string;
  name: string;
  sku: string;
  description?: string;
  quantity: number;
  allocatedQuantity?: number;
  minStock: number;
  maxStock?: number;
  unitCost: number;
  unit: InventoryUnit;
  itemGroup?: string | ItemGroup;
  vendor?: string | Vendor;
  company: string;
  createdBy?: string;
  isActive: boolean;
  hasVariants: boolean;
  variants: ItemVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface StockMovement {
  _id: string;
  item: string | InventoryItem;
  variant?: string;
  type: StockMovementType;
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  reason?: string;
  reference?: string;
  purchaseOrder?: string | {
    _id: string;
    poNumber: string;
  };
  // FIFO cost tracking
  unitCost?: number;
  totalCost?: number;
  batchBreakdown?: BatchBreakdownItem[];
  company: string;
  createdBy?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

// FIFO Batch Types
export interface BatchBreakdownItem {
  batchId: string;
  quantity: number;
  unitCost: number;
}

export interface BatchPreviewItem {
  batchId: string;
  quantity: number;
  unitCost: number;
  subtotal: number;
  batchDate: string;
  reference?: string;
}

export interface StockOutPreview {
  totalQuantity: number;
  totalCost: number;
  weightedAverageCost: number;
  batches: BatchPreviewItem[];
  insufficientStock: boolean;
  availableQuantity: number;
}

export interface StockBatch {
  _id: string;
  item: string;
  variant?: string;
  unitCost: number;
  initialQuantity: number;
  remainingQuantity: number;
  reference?: string;
  notes?: string;
  company: string;
  createdBy?: string;
  isMigrated: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// API Response Types
// ============================================

export interface InventoryOverviewStats {
  totalItems: number;
  lowStockCount: number;
  totalValue: number;
  totalGroups: number;
}

export interface ItemsQueryParams {
  search?: string;
  groupId?: string;
  lowStockOnly?: boolean;
  includeInactive?: boolean;
}

// ============================================
// Variant DTOs
// ============================================

export interface CreateItemVariantDto {
  attributes?: Record<string, string>;
  quantity?: number;
  unitCost?: number;
  minStock?: number;
}

export interface UpdateItemVariantDto {
  attributes?: Record<string, string>;
  unitCost?: number;
  minStock?: number;
}

// ============================================
// Vendor DTOs
// ============================================

export interface CreateVendorDto {
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  isPreferred?: boolean;
}

export interface UpdateVendorDto {
  name?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  website?: string;
  isPreferred?: boolean;
}

// ============================================
// Purchase Order Types
// ============================================

export enum PurchaseOrderStatus {
  CONFIRMED = 'confirmed',
  PARTIAL_RECEIVED = 'partial_received',
  RECEIVED = 'received',
  CANCELLED = 'cancelled',
}

export enum DeliveryType {
  DELIVERY = 'delivery',
  PICKUP = 'pickup',
}

export interface PurchaseOrderItem {
  _id?: string;
  item: string | InventoryItem;
  variant?: string | ItemVariant;
  quantity: number;
  unitCost: number;
  quantityReceived?: number;
}

export interface PurchaseOrder {
  _id: string;
  poNumber: string;
  vendor: string | Vendor;
  deliveryType: DeliveryType;
  expectedDate: string;
  status: PurchaseOrderStatus;
  items: PurchaseOrderItem[];
  notes?: string;
  subtotal: number;
  company: string;
  createdBy?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  receivedAt?: string;
  receivedBy?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreatePurchaseOrderItemDto {
  item: string;
  variant?: string;
  quantity: number;
  unitCost: number;
}

export interface CreatePurchaseOrderDto {
  vendor: string;
  deliveryType: DeliveryType;
  expectedDate: string;
  items: CreatePurchaseOrderItemDto[];
  notes?: string;
}

export interface UpdatePurchaseOrderItemDto {
  item?: string;
  variant?: string;
  quantity?: number;
  unitCost?: number;
}

export interface UpdatePurchaseOrderDto {
  vendor?: string;
  deliveryType?: DeliveryType;
  expectedDate?: string;
  items?: UpdatePurchaseOrderItemDto[];
  notes?: string;
  status?: PurchaseOrderStatus;
}

export interface PurchaseOrdersQueryParams {
  status?: PurchaseOrderStatus;
  vendorId?: string;
}

// Receive Delivery DTOs
export interface ReceiveDeliveryItemDto {
  poItemId: string;
  quantityReceived: number;
}

export interface ReceiveDeliveryDto {
  items: ReceiveDeliveryItemDto[];
  notes?: string;
}

// ============================================
// Pick List Types
// ============================================

export enum PickListStatus {
  PENDING = 'pending',
  PARTIAL = 'partial',
  FULFILLED = 'fulfilled',
  CANCELLED = 'cancelled',
}

export enum PickListJobType {
  PROJECT = 'project',
  WORK_ORDER = 'work_order',
}

export interface BatchAllocation {
  batchId: string;
  quantity: number;
  unitCost: number;
}

export interface PickListLineItem {
  _id: string;
  item: string | InventoryItem;
  variant?: string | ItemVariant;
  quantityRequested: number;
  quantityFulfilled: number;
  batchAllocations?: BatchAllocation[];
  notes?: string;
  unitCost?: number;
  totalCost?: number;
}

export interface FulfillmentRecord {
  _id: string;
  fulfilledBy: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  items: Array<{
    lineItemId: string;
    quantity: number;
    unitCost: number;
    totalCost: number;
  }>;
  notes?: string;
  createdAt: string;
}

export interface PickList {
  _id: string;
  pickListNumber: string;
  jobType: PickListJobType;
  project?: string | {
    _id: string;
    name: string;
    status: string;
  };
  workOrder?: string | {
    _id: string;
    title: string;
    status: string;
  };
  status: PickListStatus;
  items: PickListLineItem[];
  notes?: string;
  fulfillmentHistory: FulfillmentRecord[];
  company: string;
  createdBy?: string | {
    _id: string;
    firstName: string;
    lastName: string;
  };
  fulfilledAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Pick List DTOs
export interface CreatePickListLineItemDto {
  item: string;
  variant?: string;
  quantityRequested: number;
  notes?: string;
}

export interface CreatePickListDto {
  jobType: PickListJobType;
  project?: string;
  workOrder?: string;
  items: CreatePickListLineItemDto[];
  notes?: string;
}

export interface FulfillPickListLineItemDto {
  lineItemId: string;
  quantityFulfilled: number;
}

export interface FulfillPickListDto {
  items: FulfillPickListLineItemDto[];
  notes?: string;
}

export interface PickListsQueryParams {
  status?: PickListStatus;
  jobType?: PickListJobType;
  projectId?: string;
  workOrderId?: string;
}
